import { useRef, useCallback } from "react";
import { useDebugStore } from "@/stores/useDebugStore";
import { useUIStore } from "@/stores/useUIStore";
import { useTier } from "@/hooks/useTier";
import { can } from "@/lib/tier";

const REQUIRED_CLICKS = 6;
const TIMEOUT_MS = 3000;

/**
 * Returns an onClick handler that toggles debug mode after 6 rapid clicks
 * within 3 seconds. Fires a toast on activation/deactivation.
 */
export function useDebugGesture() {
  const tier = useTier();
  const clickCount = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onClick = useCallback(() => {
    if (!can(tier, "onchainDetail")) return;
    clickCount.current += 1;

    // Reset timer on each click
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      clickCount.current = 0;
    }, TIMEOUT_MS);

    if (clickCount.current >= REQUIRED_CLICKS) {
      clickCount.current = 0;
      if (timer.current) clearTimeout(timer.current);

      const { toggle, enabled } = useDebugStore.getState();
      toggle();

      const willBeEnabled = !enabled;
      useUIStore.getState().addToast({
        type: "info",
        message: willBeEnabled ? "Debug mode enabled" : "Debug mode disabled",
      });
    }
  }, [tier]);

  return { onClick };
}
